import { useCallback, useEffect, useState } from "react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { RxDragHandleDots2 } from "react-icons/rx";
import { PiImageBold, PiMicrophoneBold, PiTextAlignRightBold, PiVideoCameraBold } from "react-icons/pi";
import Pagination from "../../components/common/pagination/Pagination";
import Title from "../../components/common/title/Title";
import Button from "../../components/common/button/Button";
import { getApi, deleteApi, putApi } from "../../services/api/globalApi";
import { successMessage, errorMessage } from "../../utils/toastify-message/ToastifyMessage";
import { EXPERIENCE_TYPES, TYPE_LABEL, apiErrorMessage, formatDuration, publicFileUrl, toFaDigits } from "./mediaUtils";
import { STATUS_META, STATUS_TABS } from "./statusMeta";
import ReviewModal from "./ReviewModal";

const API_PATH = "admin/customer-experiences";
const inputClass =
  "bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white";

const TYPE_ICON = {
  video: PiVideoCameraBold,
  voice: PiMicrophoneBold,
  photo: PiImageBold,
  text: PiTextAlignRightBold,
};

function MediaThumb({ item }) {
  const Icon = TYPE_ICON[item.type] || PiTextAlignRightBold;
  const thumb = item.type === "video" ? item.posterSrc : item.type === "photo" ? item.imageSrc : "";
  if (thumb) {
    return (
      <span className="relative block h-12 w-12 overflow-hidden rounded-lg bg-gray-900">
        <img src={publicFileUrl(thumb)} alt="" className="h-full w-full object-cover" />
        <Icon size={14} className="absolute bottom-1 left-1 text-white drop-shadow" />
      </span>
    );
  }
  return (
    <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400">
      <Icon size={20} />
    </span>
  );
}

function SortableRow({ item, index, onDelete, onToggle, onReview }) {
  const status = STATUS_META[item.status] || STATUS_META.published;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };

  return (
    <tr ref={setNodeRef} style={style} className="border-b bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-600">
      <td className="px-3 py-3">
        <button {...attributes} {...listeners} type="button" aria-label="جابه‌جایی" className="cursor-grab text-gray-400 hover:text-gray-600">
          <RxDragHandleDots2 size={18} />
        </button>
      </td>
      <td className="px-3 py-3"><MediaThumb item={item} /></td>
      <td className="px-3 py-3 font-medium text-gray-900 dark:text-white">
        {item.name}
        <span className="block text-xs font-normal text-gray-500" dir="ltr">{item.user?.mobile || "—"}</span>
      </td>
      <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-300">
        {TYPE_LABEL[item.type] || item.type}
        {item.durationSec > 0 && <span className="block text-xs text-gray-400" dir="ltr">{formatDuration(item.durationSec)}</span>}
      </td>
      <td className="max-w-[170px] truncate px-3 py-3 text-sm text-gray-500" title={item.text}>{item.text}</td>
      <td className="px-3 py-3 text-xs text-gray-600 dark:text-gray-300">
        <span className="block max-w-[150px] truncate" title={item.productName}>{item.productName}</span>
        <span className="text-[11px] text-gray-400" dir="ltr">{item.productSku}</span>
      </td>
      <td className="px-3 py-3 text-center text-sm">{toFaDigits(index + 1)}</td>
      <td className="px-3 py-3">
        <span className={`inline-block whitespace-nowrap rounded px-2 py-1 text-xs ${status.cls}`}>{status.label}</span>
        {item.status === "published" && (
          <button
            type="button"
            onClick={() => onToggle(item)}
            className={`mt-1 block rounded px-2 py-0.5 text-[11px] ${item.isActive ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}
            title="نمایش یا مخفی کردن در سایت"
          >
            {item.isActive ? "نمایش در سایت" : "مخفی"}
          </button>
        )}
      </td>
      <td className="px-3 py-3">
        <div className="flex gap-3">
          <button type="button" onClick={() => onReview(item)} className="text-sm font-medium text-green-700 hover:underline dark:text-green-400">
            {item.status === "pending" ? "بررسی" : "مشاهده"}
          </button>
          <button type="button" onClick={() => onDelete(item)} className="text-sm font-medium text-red-600 hover:underline dark:text-red-400">حذف</button>
        </div>
      </td>
    </tr>
  );
}

export default function CustomerExperienceList() {
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [filter, setFilter] = useState({});
  const [search, setSearch] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [reviewing, setReviewing] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const fetchData = useCallback(async (params = {}) => {
    try {
      const res = await getApi({ path: API_PATH, params });
      setData((res?.data?.result?.data || []).map((item) => ({ ...item, id: item._id })));
      setPagination(res?.data?.result);
      setHasChanges(false);
    } catch (error) {
      errorMessage(apiErrorMessage(error, "خطا در دریافت تجربه‌ها"));
    }
  }, []);

  useEffect(() => {
    fetchData({});
  }, [fetchData]);

  const filterDataHandler = (newParams) => {
    const merged = { ...filter, ...newParams };
    setFilter(merged);
    fetchData(merged);
  };

  const applySearch = (e) => {
    e.preventDefault();
    filterDataHandler({ searchText: search.trim() || undefined, page: 1 });
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = data.findIndex((i) => i.id === active.id);
    const newIndex = data.findIndex((i) => i.id === over.id);
    setData((items) => arrayMove(items, oldIndex, newIndex));
    setHasChanges(true);
  };

  const savePriorities = async () => {
    try {
      const top = Math.max(...data.map((i) => i.priority || 0), data.length);
      const items = data.map((item, index) => ({ id: item._id, priority: top - index }));
      await putApi({ path: `${API_PATH}/update-priorities`, body: { items } });
      successMessage("ترتیب نمایش ذخیره شد");
      fetchData(filter);
    } catch (error) {
      errorMessage(apiErrorMessage(error, "خطا در ذخیره ترتیب"));
    }
  };

  const toggleActive = async (item) => {
    try {
      await putApi({ path: `${API_PATH}/${item._id}`, body: { isActive: !item.isActive } });
      setData((items) => items.map((i) => (i._id === item._id ? { ...i, isActive: !item.isActive } : i)));
    } catch (error) {
      errorMessage(apiErrorMessage(error, "خطا در تغییر وضعیت"));
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`حذف تجربه‌ی «${item.name}»؟`)) return;
    try {
      await deleteApi({ path: `${API_PATH}/${item._id}` });
      successMessage("تجربه حذف شد");
      fetchData(filter);
    } catch (error) {
      errorMessage(apiErrorMessage(error, "خطا در حذف"));
    }
  };

  return (
    <div className="rounded-3xl bg-white p-6 dark:bg-gray-900">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <Title title="بررسی تجربه‌های مشتریان" />
        <div className="flex gap-2">
          {hasChanges && (
            <Button onClick={savePriorities} text="ذخیره ترتیب" color="primary" type="button" className="min-w-fit whitespace-nowrap" />
          )}
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <form onSubmit={applySearch} className="flex gap-2">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="جستجو در نام، شهر، متن یا محصول"
            className={`${inputClass} w-72`}
          />
          <button type="submit" className="rounded-lg bg-blue-700 px-4 text-sm text-white hover:bg-blue-800">جستجو</button>
        </form>
        <select
          value={filter.type || ""}
          onChange={(e) => filterDataHandler({ type: e.target.value || undefined, page: 1 })}
          className={inputClass}
          aria-label="نوع تجربه"
        >
          <option value="">همه‌ی انواع</option>
          {EXPERIENCE_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
        <select
          value={filter.status || ""}
          onChange={(e) => filterDataHandler({ status: e.target.value || undefined, page: 1 })}
          className={inputClass}
          aria-label="وضعیت تجربه"
        >
          {STATUS_TABS.map((tab) => (
            <option key={tab.value || "all"} value={tab.value}>{tab.label}</option>
          ))}
        </select>
      </div>

      <Pagination pagination={pagination} filterDataHandler={filterDataHandler} />

      <div className="relative mt-6 overflow-x-auto shadow-md sm:rounded-lg">
        {data.length ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <table className="w-full text-left text-xs text-gray-500 rtl:text-right sm:text-sm dark:text-gray-400">
              <thead className="bg-gray-50 text-xs uppercase text-gray-700 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th className="w-8 px-3 py-3"></th>
                  <th className="w-16 px-3 py-3">رسانه</th>
                  <th className="px-3 py-3">مشتری</th>
                  <th className="px-3 py-3">نوع</th>
                  <th className="px-3 py-3">متن</th>
                  <th className="px-3 py-3">محصول</th>
                  <th className="w-16 px-3 py-3 text-center">ترتیب</th>
                  <th className="px-3 py-3">وضعیت</th>
                  <th className="px-3 py-3">عملیات</th>
                </tr>
              </thead>
              <SortableContext items={data.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                <tbody>
                  {data.map((item, index) => (
                    <SortableRow
                      key={item.id}
                      item={item}
                      index={index}
                      onDelete={handleDelete}
                      onToggle={toggleActive}
                      onReview={setReviewing}
                    />
                  ))}
                </tbody>
              </SortableContext>
            </table>
          </DndContext>
        ) : (
          <div className="flex items-center justify-center py-10 dark:text-white">
            <p>موردی برای نمایش وجود ندارد.</p>
          </div>
        )}
      </div>

      <Pagination pagination={pagination} filterDataHandler={filterDataHandler} />

      {reviewing && (
        <ReviewModal
          item={reviewing}
          onClose={() => setReviewing(null)}
          onDone={() => {
            setReviewing(null);
            fetchData(filter);
          }}
        />
      )}
    </div>
  );
}
